import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Portal, Transition } from "@headlessui/react";
import clsx from "clsx";

/**
 * Cards animated with `animate-fade-in` keep a `transform` after the animation (fill-mode
 * `both`), so every row and every chase item is its own stacking context — a `z-[99999]`
 * on an absolutely positioned menu inside one can never paint over the next sibling.
 * Panels therefore render into a portal on <body> and are pinned to their trigger with
 * fixed coordinates, which puts them above everything regardless of where they came from.
 */

const VIEWPORT_MARGIN = 8;

interface IDropdownPanel {
  /** Open state from the Headless UI render prop — drives mount + transition. */
  open: boolean;
  /** The trigger the panel is pinned to. */
  anchorRef: React.RefObject<HTMLElement>;
  /** Which trigger edge the panel lines up with. */
  align?: "left" | "right";
  /** Fixed panel width; omit to size to content. */
  width?: number;
  /** Distance between trigger and panel. */
  gap?: number;
  className?: string;
  children: React.ReactNode;
}

type Position = { top: number; left: number };

const DropdownPanel: React.FC<IDropdownPanel> = ({
  open,
  anchorRef,
  align = "right",
  width,
  gap = 8,
  className,
  children,
}) => {
  /**
   * `Transition` only renders its child on the commit *after* `show` flips to true, so a
   * plain ref read in an effect keyed on `open` still sees null. Holding the node in state
   * means the measure runs the moment the node attaches — during that commit, before paint.
   */
  const [panel, setPanel] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;

    if (!anchor || !panel) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    // offsetWidth/Height are pre-transform, so the enter animation's scale doesn't skew them.
    const panelWidth = width ?? panel.offsetWidth;
    const panelHeight = panel.offsetHeight;

    const spaceBelow = window.innerHeight - rect.bottom - gap - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - gap - VIEWPORT_MARGIN;
    const flipUp = panelHeight > spaceBelow && spaceAbove > spaceBelow;

    const top = flipUp
      ? Math.max(VIEWPORT_MARGIN, rect.top - gap - panelHeight)
      : rect.bottom + gap;

    const rawLeft = align === "right" ? rect.right - panelWidth : rect.left;
    const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - panelWidth - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(VIEWPORT_MARGIN, rawLeft), maxLeft);

    setPosition((prev) =>
      prev && prev.top === top && prev.left === left ? prev : { top, left }
    );
  }, [align, anchorRef, gap, panel, width]);

  // Measure before paint so the panel never flashes at the wrong spot.
  useLayoutEffect(() => {
    if (panel) {
      updatePosition();
    } else {
      setPosition(null);
    }
  }, [panel, updatePosition]);

  // Follow the trigger while the page scrolls behind an open panel, and re-measure
  // when the panel's own content changes height.
  useEffect(() => {
    if (!panel) {
      return;
    }

    const onViewportChange = () => updatePosition();
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(onViewportChange) : null;
    observer?.observe(panel);

    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
      observer?.disconnect();
    };
  }, [panel, updatePosition]);

  return (
    <Portal>
      <Transition
        show={open}
        ref={setPanel}
        as="div"
        style={{
          top: position?.top ?? 0,
          left: position?.left ?? 0,
          width,
          // Keep it out of sight for the single commit before the measure lands.
          visibility: position ? "visible" : "hidden",
        }}
        className={clsx("fixed z-[9999] w-max smallTablet:max-w-[calc(100vw-1rem)]", className)}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        {children}
      </Transition>
    </Portal>
  );
};

export default DropdownPanel;
